import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import logo from "../assets/images/logo_2.png";
import { FiTrash2, FiLogOut } from "react-icons/fi";

export default function ViewClients() {
  const [clients, setClients] = useState([]);
  const [darkMode, setDarkMode] = useState(true);
  const [editClient, setEditClient] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const location = useLocation();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line
  }, []);

  const fetchClients = () => {
    axios
      .get(`${BASE_URL}/api/logins`)
      .then((res) => {
        if (res.data && Array.isArray(res.data.data)) {
          setClients(res.data.data);
        }
      })
      .catch((err) => console.error("Error fetching clients", err));
  };

  const handleEdit = (client) => {
    setEditClient(client);
    let accs = [];
    if (client.MT4_Account) {
      accs = client.MT4_Account.replace(/^"|"$/g, "")
        .split(",")
        .map((id) => ({ id: id.trim() }))
        .filter((a) => a.id && a.id !== "");
    }
    setAccounts(accs);
  };

  const handleDelete = (sn) => {
    if (!sn) return;
    if (window.confirm("Are you sure you want to delete this client?")) {
      axios
        .delete(`${BASE_URL}/api/logins/${sn}`)
        .then(() => {
          setClients((prev) => prev.filter((c) => c.SN !== sn));
        })
        .catch((err) =>
          alert(
            "Delete failed: " + (err?.response?.data?.message || err.message)
          )
        );
    }
  };

  const addAccount = () => setAccounts([...accounts, { id: "" }]);
  const updateAccount = (idx, value) =>
    setAccounts(accounts.map((acc, i) => (i === idx ? value : acc)));
  const removeAccount = (idx) =>
    setAccounts(accounts.filter((_, i) => i !== idx));

  const handleSaveAccounts = async () => {
    if (!editClient) return;
    const idString = accounts
      .map((acc) => (acc.id || "").trim())
      .filter(Boolean)
      .join(",");
    await axios.put(
      `${BASE_URL}/api/logins/${editClient.SN}/accounts`,
      { accounts: idString }
    );
    fetchClients();
    setEditClient(null);
    setAccounts([]);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  // === Language handling ===
  const LANG_OPTIONS = [
    { value: "EN", label: "English (EN)" },
    { value: "JP", label: "日本語 (JP)" },
    { value: "FA", label: "فارسی (FA)" },
    { value: "ZH", label: "中文 (ZH)" },
  ];

  const normalizeDisplayLang = (raw) => {
    const s = String(raw || "").trim().toUpperCase();
    // Allow JP/JA, FA/IR, ZH/CN
    if (s === "JA") return "JP";
    if (s === "IR") return "FA";
    if (s === "CN") return "ZH";
    if (["EN", "JP", "FA", "ZH"].includes(s)) return s;
    return "EN";
  };

  const handleLanguageChange = async (sn, newVal) => {
    // Optimistic UI: update state immediately
    setClients((prev) =>
      prev.map((c) => (c.SN === sn ? { ...c, Language: newVal } : c))
    );
    try {
      await axios.patch(
        `${BASE_URL}/api/logins/${sn}/language`,
        { language: newVal }
      );
      // Optionally re-fetch to confirm
      // fetchClients();
    } catch (e) {
      console.error("Failed to update language:", e);
      alert("Failed to update language. Reverting.");
      // revert if failed
      fetchClients();
    }
  };

  const filteredClients = clients.filter(
    (client) =>
      (client.Name || "").toLowerCase().includes(search.toLowerCase()) ||
      (client.Username || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div
      className={
        darkMode
          ? "min-h-screen bg-gradient-to-b from-[#0B0F2B] via-[#11152F] to-[#0B0F2B] text-white"
          : "min-h-screen bg-gradient-to-b from-white via-gray-100 to-white text-black"
      }
    >
      {/* Mobile Header */}
      {isMobile && (
        <div
          className={`w-full px-4 py-3 flex justify-between items-center ${
            darkMode ? "bg-[#13162C]" : "bg-gray-100"
          } sticky top-0 z-50`}
        >
          <div className="flex items-center gap-2">
            <img src={logo} className="h-8" alt="Logo" />
            <h1
              className={`text-lg font-bold ${
                darkMode ? "text-white" : "text-black"
              }`}
            >
              View Clients
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <label className="inline-flex relative items-center cursor-pointer">
              <input
                type="checkbox"
                checked={darkMode}
                onChange={() => setDarkMode(!darkMode)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
            <button
              onClick={handleLogout}
              className="bg-red-600 p-2 rounded text-sm"
            >
              <FiLogOut className="text-white" />
            </button>
          </div>
        </div>
      )}

      {/* Desktop Header */}
      {!isMobile && (
        <nav
          className={
            darkMode
              ? "w-full px-6 py-4 flex justify-between items-center bg-[#13162C] shadow-md sticky top-0 z-50"
              : "w-full px-6 py-4 flex justify-between items-center bg-gray-100 shadow-md sticky top-0 z-50"
          }
        >
          <div className="flex items-center gap-3">
            <img src={logo} className="h-10" alt="Logo" />
            <h1 className="text-2xl font-bold text-white">Account Dashboard</h1>
          </div>
          <div className="flex items-center gap-4 flex-wrap justify-center w-full max-w-[700px]">
            <Link to="/dashboard">
              <button
                className={`px-3 py-1 rounded text-sm transition ${
                  location.pathname === "/dashboard"
                    ? "bg-[#1FB0FF] hover:bg-[#1597d6]"
                    : darkMode
                    ? "hover:bg-gray-700"
                    : "hover:bg-gray-300"
                }`}
              >
                Dashboard
              </button>
            </Link>
            <Link to="/AddClient">
              <button
                className={`px-3 py-1 rounded text-sm transition ${
                  location.pathname === "/AddClient"
                    ? "bg-[#1FB0FF] hover:bg-[#1597d6]"
                    : darkMode
                    ? "hover:bg-gray-700"
                    : "hover:bg-gray-300"
                }`}
              >
                Add Client
              </button>
            </Link>
            <Link to="/Viewclients">
              <button
                className={`px-3 py-1 rounded text-sm transition ${
                  location.pathname === "/Viewclients"
                    ? "bg-[#1FB0FF] hover:bg-[#1597d6]"
                    : darkMode
                    ? "hover:bg-gray-700"
                    : "hover:bg-gray-300"
                }`}
              >
                View/Edit Clients
              </button>
            </Link>
            <Link to="/AlertsSettings">
              <button
                className={`px-3 py-1 rounded text-sm transition ${
                  location.pathname === "/AlertsSettings"
                    ? "bg-[#1FB0FF] hover:bg-[#1597d6]"
                    : darkMode
                    ? "hover:bg-gray-700"
                    : "hover:bg-gray-300"
                }`}
              >
                Alerts Settings
              </button>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">{darkMode ? "🌙" : "☀️"}</span>
            <label className="inline-flex relative items-center cursor-pointer">
              <input
                type="checkbox"
                checked={darkMode}
                onChange={() => setDarkMode(!darkMode)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
            <button
              onClick={handleLogout}
              className="bg-red-600 px-4 py-2 rounded text-sm flex items-center gap-1"
            >
              <FiLogOut /> Logout
            </button>
          </div>
        </nav>
      )}

      {/* Mobile Menu */}
      {isMobile && (
        <div
          className={`w-full px-2 py-2 flex justify-between items-center ${
            darkMode ? "bg-gray-900" : "bg-gray-200"
          } shadow-sm`}
        >
          <Link to="/dashboard" className="text-center px-2 py-1 text-sm">
            Dashboard
          </Link>
          <Link to="/AddClient" className="text-center px-2 py-1 text-sm">
            Add Client
          </Link>
          <Link to="/Viewclients" className="text-center px-2 py-1 text-sm">
            View Clients
          </Link>
          <Link to="/AlertsSettings" className="text-center px-2 py-1 text-sm">
            Alerts
          </Link>
        </div>
      )}

      {/* SEARCH BAR */}
      <div
        className={`flex ${
          isMobile ? "flex-col px-4 py-3 gap-3" : "justify-between px-6 py-4"
        }`}
      >
        <h2 className={`${isMobile ? "text-xl" : "text-2xl"} font-bold`}>
          View/Edit Clients
        </h2>
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${
            isMobile ? "w-full" : "w-64"
          } ${
            darkMode
              ? "bg-[#181C3F] border rounded px-4 py-2 text-sm text-white"
              : "bg-gray-200 border rounded px-4 py-2 text-sm text-black"
          }`}
        />
      </div>

      {/* TABLE SECTION */}
      <div className={`${isMobile ? "px-2" : "px-4"} pb-8 overflow-x-auto`}>
        <table
          className={`${
            isMobile ? "min-w-[800px]" : "min-w-full"
          } text-sm ${
            darkMode
              ? "bg-[#101436] border border-blue-800"
              : "bg-white border border-gray-300"
          }`}
        >
          <thead
            className={darkMode ? "bg-[#181C3F] text-white" : "bg-gray-200 text-black"}
          >
            <tr>
              <th className="px-4 py-3 text-left uppercase text-xs font-semibold tracking-wider border-b border-gray-700 whitespace-nowrap">
                #
              </th>
              <th className="px-4 py-3 text-left uppercase text-xs font-semibold tracking-wider border-b border-gray-700 whitespace-nowrap">
                Name
              </th>
              {!isMobile && (
                <>
                  <th className="px-4 py-3 text-left uppercase text-xs font-semibold tracking-wider border-b border-gray-700 whitespace-nowrap">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left uppercase text-xs font-semibold tracking-wider border-b border-gray-700 whitespace-nowrap">
                    Password
                  </th>
                </>
              )}
              <th className="px-4 py-3 text-left uppercase text-xs font-semibold tracking-wider border-b border-gray-700 whitespace-nowrap max-w-xs">
                MT5 Accounts
              </th>
              {!isMobile && (
                <th className="px-4 py-3 text-left uppercase text-xs font-semibold tracking-wider border-b border-gray-700 whitespace-nowrap">
                  Language
                </th>
              )}
              <th className="px-4 py-3 text-left uppercase text-xs font-semibold tracking-wider border-b border-gray-700 whitespace-nowrap">
                Status
              </th>
              <th className="px-4 py-3 text-left uppercase text-xs font-semibold tracking-wider border-b border-gray-700 whitespace-nowrap">
                Action
              </th>
            </tr>
          </thead>
          <tbody
            className={
              darkMode
                ? "divide-y divide-blue-900 text-white"
                : "divide-y divide-gray-200 text-black"
            }
          >
            {filteredClients.map((client, index) => {
              const currentLang = normalizeDisplayLang(client.Language);
              return (
                <tr
                  key={client.SN || index}
                  className={
                    darkMode
                      ? "hover:bg-[#1A1E3F] transition"
                      : "hover:bg-gray-100 transition"
                  }
                >
                  <td className="px-4 py-3 whitespace-nowrap">{index + 1}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{client.Name}</td>
                  {!isMobile && (
                    <>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {client.Username}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {client.Password}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap max-w-xs overflow-hidden text-ellipsis">
                    <button
                      onClick={() => handleEdit(client)}
                      className="ml-2 bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs"
                    >
                      Show
                    </button>
                  </td>

                  {!isMobile && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      <select
                        className={`rounded px-2 py-1 text-sm ${
                          darkMode
                            ? "bg-[#1F2235] border border-gray-600 text-white"
                            : "bg-gray-100 border border-gray-400 text-black"
                        }`}
                        value={currentLang}
                        onChange={(e) =>
                          handleLanguageChange(client.SN, e.target.value)
                        }
                      >
                        {LANG_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  )}

                  <td className="px-4 py-3 whitespace-nowrap">
                    <label className="inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only" checked readOnly />
                      <div className="relative w-10 h-5 rounded-full bg-green-500">
                        <span className="absolute left-1 top-0.5 w-4 h-4 bg-white rounded-full transition transform translate-x-5"></span>
                      </div>
                    </label>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      onClick={() => handleDelete(client.SN)}
                      className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded"
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p
          className={`${
            darkMode ? "text-gray-400" : "text-gray-600"
          } text-xs mt-3 px-2`}
        >
          Showing {filteredClients.length} of {clients.length} entries
        </p>
      </div>

      {/* ACCOUNTS MODAL */}
      {editClient && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
          <div
            className={`${
              darkMode ? "bg-[#1F233F] text-white" : "bg-white text-black"
            } p-4 md:p-6 rounded-xl shadow-md w-[90%] max-w-2xl`}
          >
            <h3 className="text-lg font-semibold mb-4">
              MT5/MT4 Account IDs for {editClient.Name}
            </h3>
            <div className="space-y-3 max-h-72 overflow-auto">
              {accounts.map((acc, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={acc.id}
                    placeholder="Account ID"
                    onChange={(e) => updateAccount(i, { id: e.target.value })}
                    className={`w-full md:w-52 border ${
                      darkMode
                        ? "border-gray-600 bg-[#13162C] text-white"
                        : "border-gray-400 bg-white text-black"
                    } rounded px-3 py-1`}
                  />
                  <button
                    onClick={() => removeAccount(i)}
                    className="bg-red-600 px-2 rounded text-white text-lg"
                    title="Remove"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ))}
              <button
                onClick={addAccount}
                className="mt-2 bg-green-600 px-3 py-1 rounded text-white"
              >
                + Add Another Account
              </button>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setEditClient(null)}
                className={`px-3 py-1 ${
                  darkMode ? "bg-gray-600" : "bg-gray-400"
                } rounded`}
              >
                Close
              </button>
              <button
                onClick={handleSaveAccounts}
                className="px-3 py-1 bg-blue-600 rounded text-white"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
